/**
 * 连接韧性 — 断线队列 + 重连策略 + 状态同步
 */

export interface QueuedMessage {
  id: string
  command: string
  payload: any
  queuedAt: number
  priority: number
}

export interface ReconnectStrategy {
  maxAttempts: number
  baseDelay: number
  maxDelay: number
  factor: number
}

export class ConnectionResilience {
  private queue: QueuedMessage[] = []
  private maxQueueSize: number
  private strategy: ReconnectStrategy
  private attempt = 0
  private connected = false
  private stateVersion = 0
  private pendingSync: Record<string, any> = {}
  private idCounter = 0

  constructor(options: { maxQueueSize?: number; strategy?: Partial<ReconnectStrategy> } = {}) {
    this.maxQueueSize = options.maxQueueSize ?? 1000
    this.strategy = {
      maxAttempts: options.strategy?.maxAttempts ?? 10,
      baseDelay: options.strategy?.baseDelay ?? 1000,
      maxDelay: options.strategy?.maxDelay ?? 30000,
      factor: options.strategy?.factor ?? 2,
    }
  }

  /** 入队消息（断线时） */
  enqueue(command: string, payload: any, priority = 0): string | null {
    if (this.queue.length >= this.maxQueueSize) return null
    const id = `q_${++this.idCounter}`
    this.queue.push({ id, command, payload, queuedAt: Date.now(), priority })
    this.queue.sort((a, b) => b.priority - a.priority)
    return id
  }

  /** 获取待发送队列 */
  drain(): QueuedMessage[] {
    const items = [...this.queue]
    this.queue = []
    return items
  }

  /** 队列大小 */
  queueSize(): number {
    return this.queue.length
  }

  /** 计算下次重连延迟 */
  getNextDelay(): number {
    const delay = Math.min(this.strategy.baseDelay * Math.pow(this.strategy.factor, this.attempt), this.strategy.maxDelay)
    this.attempt++
    return delay
  }

  /** 是否还能重连 */
  canReconnect(): boolean {
    return this.attempt < this.strategy.maxAttempts
  }

  /** 重置重连计数 */
  resetAttempts(): void {
    this.attempt = 0
  }

  /** 获取重连次数 */
  getAttempts(): number {
    return this.attempt
  }

  /** 标记连接状态 */
  setConnected(connected: boolean): void {
    this.connected = connected
    if (connected) this.resetAttempts()
  }

  /** 是否连接 */
  isConnected(): boolean {
    return this.connected
  }

  /** 记录待同步状态 */
  markForSync(key: string, value: any): void {
    this.pendingSync[key] = value
    this.stateVersion++
  }

  /** 获取待同步数据 */
  getPendingSync(): Record<string, any> {
    const data = { ...this.pendingSync }
    this.pendingSync = {}
    return data
  }

  /** 获取状态版本 */
  getStateVersion(): number {
    return this.stateVersion
  }
}
